import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Paper,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Menu, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Tune';
import { alpha } from '@mui/material/styles';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { orderService } from '../services/orderService';
import { orderTransferService } from '../services/orderTransferService';
import { ecommerceApi, n11Api } from '../../../config/apiClient';
import { NETAPI_URL } from '../../../config/api';
import { infiniteRetryPolicy, startResilientConnection } from '../../../utils/signalRConnection';
import OrderSummaryCards from '../components/OrderSummaryCards';
import OrderFilters from '../components/OrderFilters';
import OrderTable from '../components/OrderTable';
import CreateOrderModal from '../components/CreateOrderModal';
import OrderDetailModal from '../components/OrderDetailModal';
import CreateWaveModal from '../components/CreateWaveModal';
import GuideButton from '../../common/guide/GuideButton';

interface Warehouse { id: string; name: string; }

interface OrderSummary {
  totalOrders: number; pendingOrders: number; awaitingStockOrders: number;
  inWaveOrders: number; pickingOrders: number; packingOrders: number;
  packedOrders: number; shippedOrders: number; cancelledOrders: number;
  singleOrders: number; multiOrders: number;
}

interface WmsOrder {
  id: string; orderNumber: string; customerName: string;
  orderType: string; itemCount: number; status: string;
  priority: number; createdAt: string;
  sourceType?: string;
  sourceOrderId?: string;
  desi?: number | null;
  totalDesi?: number | null;
  totalDesiValue?: number | null;
  assignedPersonnelId?: string;
  assignedPersonnelName?: string;
}

const ORDER_SOURCE_BUTTONS = [
  { id: 'all', label: 'Yeni Siparişler' },
  { id: 'ecommerce', label: 'E-Ticaret' },
  { id: 'trendyol', label: 'Trendyol' },
  { id: 'hepsiburada', label: 'Hepsiburada' },
  { id: 'n11', label: 'N11' },
  { id: 'pazarama', label: 'Pazarama' },
  { id: 'pttavm', label: 'PttAVM' },
] as const;
type OrderSourceFilter = (typeof ORDER_SOURCE_BUTTONS)[number]['id'];
const ORDER_SOURCE_OVERRIDE_STORAGE_KEY = 'wms_order_source_overrides';
const WMS_WAVE_CREATED_EVENT = 'wms-wave-created';
const WMS_WAVE_CREATED_STORAGE_KEY = 'wms-wave-created-ts';
const WMS_WAVE_SETTINGS_STORAGE_KEY = 'wms-wave-settings';
const N11_POLL_INTERVAL_MS = 15000;
const N11_FAILED_RETRY_COOLDOWN_MS = 5 * 60 * 1000;
const N11_ELIGIBLE_STATUSES = new Set([
  'paid',
  'confirmed',
  'received',
  'pending',
  'shipped',
  'delivered',
]);

const resolveCompanyId = (): string | null => {
  try {
    const fromUserDetails = localStorage.getItem('userDetails');
    if (fromUserDetails) {
      const parsed = JSON.parse(fromUserDetails);
      if (parsed?.companyId) return String(parsed.companyId);
    }
  } catch {
    // no-op
  }
  return localStorage.getItem('__customerDisplayCompanyId');
};

const toIsoOrNull = (value?: string | number | null): string | null => {
  if (value === null || value === undefined || value === '') return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

/** N11 API `lines` döner; eski payload'larda `items` olabilir. */
const extractN11ItemsForSync = (order: any): any[] => {
  if (!order || typeof order !== 'object') return [];
  const candidates = [order.items, order.lines, order.orderItems, order.orderItemList];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }
  return [];
};

const mapN11OrdersToMarketplaceSyncPayload = (orders: any[]) => {
  return orders.map((order: any) => ({
    OrderId: order.id || '',
    OrderNumber: order.orderNumber || '',
    MarketplaceOrderId: order.id || order.n11OrderId || '',
    MarketplaceOrderNumber: order.n11OrderNumber || order.orderNumber || '',
    MarketplaceStatus: order.n11Status || null,
    Date: toIsoOrNull(order.date),
    Status: order.status || 'pending',
    TotalAmount: order.totalAmount || 0,
    ItemsTotal: order.itemsTotal || 0,
    CustomerName: order.customerName || null,
    CustomerEmail: order.customerEmail || null,
    CustomerPhone: order.shippingAddress?.phone || null,
    ShippingAddress: JSON.stringify(order.shippingAddress || {}),
    BillingAddress: JSON.stringify(order.billingAddress || {}),
    CargoInfo: JSON.stringify(order.cargoInfo || {}),
    ShipmentPackageStatus: order.shipmentPackageStatus || null,
    PackageHistories: JSON.stringify(order.packageHistories || []),
    MarketplaceLastModified: toIsoOrNull(order.n11LastModified),
    Items: JSON.stringify(extractN11ItemsForSync(order))
  }));
};

const SOURCE_BRAND_META: Record<(typeof ORDER_SOURCE_BUTTONS)[number]['id'], { short: string; bg: string; fg: string }> = {
  all: { short: 'Y', bg: '#546e7a', fg: '#ffffff' },
  ecommerce: { short: 'E', bg: '#1976d2', fg: '#ffffff' },
  trendyol: { short: 'TY', bg: '#f57c00', fg: '#ffffff' },
  hepsiburada: { short: 'HB', bg: '#ff6f00', fg: '#ffffff' },
  n11: { short: 'N11', bg: '#8e24aa', fg: '#ffffff' },
  pazarama: { short: 'PZ', bg: '#e6007e', fg: '#ffffff' },
  pttavm: { short: 'PTT', bg: '#e30613', fg: '#ffffff' },
};

const normalizeOrderSource = (value?: string): Exclude<OrderSourceFilter, 'all'> | null => {
  if (!value) return null;
  const normalized = value.toLowerCase().trim();
  if (normalized.includes('trendyol')) return 'trendyol';
  if (normalized.includes('hepsiburada') || normalized.includes('hepsi')) return 'hepsiburada';
  if (normalized === 'n11') return 'n11';
  if (normalized.includes('pazarama')) return 'pazarama';
  if (normalized.includes('pttavm')) return 'pttavm';
  if (normalized.includes('ecommerce') || normalized.includes('manual') || normalized.includes('web')) return 'ecommerce';
  return null;
};

const fallbackSourceFromOrder = (order: Pick<WmsOrder, 'id' | 'orderNumber'>): Exclude<OrderSourceFilter, 'all'> => {
  const code = `${order.orderNumber || ''}-${order.id || ''}`;
  let hash = 0;
  for (let i = 0; i < code.length; i += 1) hash = (hash * 31 + code.charCodeAt(i)) >>> 0;
  const pool: Exclude<OrderSourceFilter, 'all'>[] = ['ecommerce', 'trendyol', 'hepsiburada', 'n11', 'pazarama', 'pttavm'];
  return pool[hash % pool.length];
};

const resolveOrderType = (orderType: string, itemCount: number): 'Single' | 'Multi' => {
  // Is kurali: toplam adet 1'den buyukse siparis daima Multi'dir.
  if (Number(itemCount) > 1) return 'Multi';
  if (Number(itemCount) === 1) return 'Single';

  // Eski/veri uyumsuz kayitlar icin fallback.
  const normalizedType = (orderType || '').toLowerCase().trim();
  if (normalizedType === 'multi') return 'Multi';
  return 'Single';
};

const isPendingStatus = (status?: string): boolean => {
  const normalized = (status || '').toLowerCase().trim();
  return normalized === 'pending' || normalized === 'bekleyen';
};

const isOrderSelectable = (order: WmsOrder): boolean => {
  // Sadece bekleyen siparisler dalgaya alinabilir.
  return isPendingStatus(order.status);
};

const WmsOrderPool: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [orders, setOrders] = useState<WmsOrder[]>([]);
  const [summary, setSummary] = useState<OrderSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sourceFilter, setSourceFilter] = useState<OrderSourceFilter>('all');
  const [isInWaveQuickFilterActive, setIsInWaveQuickFilterActive] = useState(false);
  type ActiveFilter = OrderSourceFilter | 'inWave';
  const activeFilter: ActiveFilter = isInWaveQuickFilterActive ? 'inWave' : sourceFilter;
  // 'all' varsayılanı Pending; özet kartından / status filtresinden seçim varsa onu kullan.
  const effectiveStatusFilter =
    activeFilter === 'inWave'
      ? 'InWave'
      : activeFilter === 'all'
        ? (statusFilter || 'Pending')
        : statusFilter;
  const setActiveFilter = (next: ActiveFilter) => {
    if (next === 'inWave') {
      setIsInWaveQuickFilterActive(true);
      setSourceFilter('all');
    } else {
      setIsInWaveQuickFilterActive(false);
      setSourceFilter(next);
    }
    setPage(1);
  };
  const [sourceOverridesByOrderId, setSourceOverridesByOrderId] = useState<Record<string, Exclude<OrderSourceFilter, 'all'>>>(() => {
    try {
      const raw = localStorage.getItem(ORDER_SOURCE_OVERRIDE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isWaveModalOpen, setIsWaveModalOpen] = useState(false);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<string | null>(null);
  const [cancelDialogId, setCancelDialogId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [bulkRetryLoading, setBulkRetryLoading] = useState(false);
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isWaveSettingsModalOpen, setIsWaveSettingsModalOpen] = useState(false);
  const [waveLimits, setWaveLimits] = useState<{ maxOrders: number | ''; maxItems: number | ''; maxTotalDesi: number | '' }>({
    maxOrders: '',
    maxItems: '',
    maxTotalDesi: '',
  });

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const n11PollingInProgressRef = useRef(false);
  const failedTransferCooldownRef = useRef<Record<string, number>>({});

  useEffect(() => {
    smartWarehouseService.getAllList().then(list => {
      const mapped = list.map((w) => ({
        id: w.id,
        name: w.warehouseName,
      }));
      setWarehouses(mapped);
      if (mapped.length > 0) {
        setSelectedWarehouseId((prev) => prev || mapped[0].id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(ORDER_SOURCE_OVERRIDE_STORAGE_KEY, JSON.stringify(sourceOverridesByOrderId));
  }, [sourceOverridesByOrderId]);

  useEffect(() => {
    const raw = localStorage.getItem(WMS_WAVE_SETTINGS_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setWaveLimits({
        maxOrders: typeof parsed?.maxOrders === 'number' ? parsed.maxOrders : '',
        maxItems: typeof parsed?.maxItems === 'number' ? parsed.maxItems : '',
        maxTotalDesi: typeof parsed?.maxTotalDesi === 'number' ? parsed.maxTotalDesi : '',
      });
    } catch {
      // ignore malformed localStorage
    }
  }, []);

  const loadOrders = useCallback(async () => {
    if (!selectedWarehouseId) return;
    setLoading(true);
    try {
      const res = await orderService.getByWarehouse(selectedWarehouseId, {
        page, pageSize, status: effectiveStatusFilter, orderType: typeFilter, search: searchText,
        sortBy: 'createdAt', sortDir: 'desc',
      });
      const data = res.data?.data || res.data;
      const list = data?.items || data || [];
      const normalized = Array.isArray(list)
        ? list.map((o: WmsOrder) => ({
            ...o,
            sourceType: o.sourceType || (o as any).sourceType || (o as any).SourceType,
            sourceOrderId: o.sourceOrderId || (o as any).sourceOrderId || (o as any).SourceOrderId,
            desi: (o as any).desi ?? (o as any).Desi ?? null,
            totalDesi: (o as any).totalDesi ?? (o as any).TotalDesi ?? null,
            totalDesiValue: (o as any).totalDesiValue ?? (o as any).TotalDesiValue ?? null,
            orderType: resolveOrderType(o.orderType, o.itemCount),
          }))
        : [];
      // WMS SourceType "EcommerceOrder" oldugu icin pazaryeri bazli ayrimi
      // SourceOrderId -> EcommerceOrder.MarketplaceSource uzerinden zenginlestir.
      let enriched = normalized;
      try {
        const marketplaceResponse = await ecommerceApi.get('/api/ecommerce/orders/marketplace');
        const marketplaceData = marketplaceResponse.data;
        const marketplaceOrders = Array.isArray(marketplaceData?.data) ? marketplaceData.data : [];
        const sourceByEcomOrderId = new Map<string, string>();
        marketplaceOrders.forEach((row: any) => {
          if (row?.id && row?.marketplaceSource) {
            sourceByEcomOrderId.set(String(row.id), String(row.marketplaceSource).toLowerCase());
          }
        });

        enriched = normalized.map((order) => {
          const mappedSource = order.sourceOrderId ? sourceByEcomOrderId.get(order.sourceOrderId) : null;
          return mappedSource ? { ...order, sourceType: mappedSource } : order;
        });
      } catch {
        // Marketplace source enrich optional: failsafe for WMS listing
      }

      setOrders(enriched);
      setTotalCount(data?.totalCount ?? data?.length ?? 0);
    } catch { setOrders([]); setTotalCount(0); }
    setLoading(false);
  }, [selectedWarehouseId, page, pageSize, effectiveStatusFilter, typeFilter, searchText]);

  const loadSummary = useCallback(async () => {
    if (!selectedWarehouseId) return;
    try {
      const res = await orderService.getSummary(selectedWarehouseId);
      setSummary(res.data?.data || res.data);
    } catch { setSummary(null); }
  }, [selectedWarehouseId]);

  useEffect(() => { loadOrders(); loadSummary(); }, [loadOrders, loadSummary]);

  useEffect(() => {
    const companyId = resolveCompanyId();
    if (!companyId) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${NETAPI_URL}/ordershub`)
      .withAutomaticReconnect(infiniteRetryPolicy)
      .build();

    const handleMarketplaceSignal = (payload: any) => {
      const payloadCompanyId = payload?.companyId || payload?.CompanyId;
      if (payloadCompanyId && payloadCompanyId !== companyId) return;

      const source = String(payload?.source || payload?.type || payload?.order?.source || 'trendyol').toLowerCase();
      if (!source.includes('trendyol')) return;

      loadOrders();
      loadSummary();
    };

    connection.on('TrendyolOrderUpdated', handleMarketplaceSignal);
    connection.on('NewMarketplaceOrder', handleMarketplaceSignal);

    // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
    // ürettiği için sunucudaki grup üyeliği kaybolur.
    const stopConnection = startResilientConnection(connection, {
      label: 'WmsOrderPool',
      onConnected: () => connection.invoke('JoinCompanyGroup', companyId),
    });

    return () => {
      connection.off('TrendyolOrderUpdated', handleMarketplaceSignal);
      connection.off('NewMarketplaceOrder', handleMarketplaceSignal);
      stopConnection();
    };
  }, [loadOrders, loadSummary]);

  const handleWarehouseChange = (wId: string) => {
    setSelectedWarehouseId(wId);
    setPage(1);
    setSelectedOrderIds(new Set());
  };

  const handleStatusFilterChange = (s: string) => {
    setStatusFilter(s);
    setActiveFilter('all');
  };
  const handleTypeFilterChange = (t: string) => { setTypeFilter(t); setPage(1); };

  const handleSearchChange = (q: string) => {
    setSearchText(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const handleSummaryFilterClick = (status: string) => {
    setStatusFilter(status);
    setTypeFilter('');
    setActiveFilter('all');
  };

  const handleOpenSettingsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchorEl(event.currentTarget);
  };

  const handleCloseSettingsMenu = () => {
    setSettingsMenuAnchorEl(null);
  };

  const handleOpenWaveSettingsModal = () => {
    handleCloseSettingsMenu();
    setIsWaveSettingsModalOpen(true);
  };

  const handleCloseWaveSettingsModal = () => {
    setIsWaveSettingsModalOpen(false);
  };

  const handleSaveWaveSettings = () => {
    localStorage.setItem(WMS_WAVE_SETTINGS_STORAGE_KEY, JSON.stringify(waveLimits));
    setIsWaveSettingsModalOpen(false);
  };

  const handleLimitFieldChange = (field: 'maxOrders' | 'maxItems' | 'maxTotalDesi', value: string) => {
    setWaveLimits((prev) => ({
      ...prev,
      [field]: value === '' ? '' : Number(value),
    }));
  };

  // Demo/Fake faz: kaynak filtrelemesi istemci tarafinda yapiliyor.
  // Entegrasyon sonrasi bu state dogrudan API query parametresine tasinacak.

  const handleToggleSelect = (id: string) => {
    setSelectedOrderIds(prev => {
      const order = orders.find((o) => o.id === id);
      if (!order || !isOrderSelectable(order)) return prev;
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const selectableIds = orders.filter(isOrderSelectable).map(o => o.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedOrderIds.has(id));
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      selectableIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleViewDetail = (id: string) => {
    setSelectedOrderForDetail(id);
    setIsDetailModalOpen(true);
  };

  const handleCancelRequest = (id: string) => setCancelDialogId(id);

  const handleConfirmCancel = async () => {
    if (!cancelDialogId) return;
    setCancelLoading(true);
    try {
      await orderService.cancel(cancelDialogId, {
        ReasonCode: 'other',
        ReasonText: 'Sipariş havuzundan iptal',
        Source: 'Pool',
      });
      setCancelDialogId(null);
      loadOrders();
      loadSummary();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'İptal işlemi başarısız');
    }
    setCancelLoading(false);
  };

  const getOrderSource = useCallback((order: WmsOrder): Exclude<OrderSourceFilter, 'all'> => {
    const byOverride = order.id ? sourceOverridesByOrderId[order.id] : undefined;
    if (byOverride) return byOverride;
    const normalized = normalizeOrderSource(order.sourceType);
    if (normalized) return normalized;
    return fallbackSourceFromOrder(order);
  }, [sourceOverridesByOrderId]);

  const filteredOrders = useMemo(() => {
    let list = sourceFilter === 'all'
      ? orders
      : orders.filter((order) => getOrderSource(order) === sourceFilter);

    return list;
  }, [orders, sourceFilter, getOrderSource]);

  const displayedOrders = useMemo(() => {
    const pendingOrders = filteredOrders.filter((order) => isPendingStatus(order.status));
    const otherOrders = filteredOrders.filter((order) => !isPendingStatus(order.status));
    return [...pendingOrders, ...otherOrders];
  }, [filteredOrders]);

  const handleOrderCreated = (created?: { sourceType: string; orderNumber?: string; orderId?: string }) => {
    setIsCreateModalOpen(false);
    if (created?.orderId) {
      const normalized = normalizeOrderSource(created.sourceType);
      if (normalized) {
        setSourceOverridesByOrderId((prev) => ({ ...prev, [created.orderId as string]: normalized }));
      }
    }
    loadOrders();
    loadSummary();
  };

  const handleDetailCancelled = () => {
    setIsDetailModalOpen(false);
    loadOrders();
    loadSummary();
  };

  const handleWaveCreated = () => {
    setIsWaveModalOpen(false);
    setSelectedOrderIds(new Set());
    // Is Emirleri sayfasi aciksa otomatik yenilemesi icin global sinyal.
    window.dispatchEvent(new CustomEvent(WMS_WAVE_CREATED_EVENT, { detail: { warehouseId: selectedWarehouseId } }));
    // Farkli sekme pencereleri icin storage tetigi.
    localStorage.setItem(WMS_WAVE_CREATED_STORAGE_KEY, String(Date.now()));
    loadOrders();
    loadSummary();
  };

  const selectedOrders = displayedOrders.filter(o => selectedOrderIds.has(o.id));

  const handleBulkRetryWmsTransfer = async () => {
    setBulkRetryLoading(true);
    try {
      const result = await orderTransferService.bulkRetryAutoTransfer();
      if (result.transferred > 0 || result.failed > 0) {
        await Promise.all([loadOrders(), loadSummary()]);
      }
      const failedPreview = (result.failedOrders || [])
        .slice(0, 5)
        .map((f) => {
          const channel = (f as { marketplaceSource?: string }).marketplaceSource || '';
          const channelPrefix = channel ? `[${channel}] ` : '';
          return `• ${channelPrefix}${f.orderNumber}: ${f.reason}`;
        })
        .join('\n');
      const msg = [
        `Aktarılan: ${result.transferred}, Atlandı: ${result.skipped}, Başarısız: ${result.failed} (Toplam kontrol: ${result.total})`,
        failedPreview ? `\nBaşarısız örnekler:\n${failedPreview}` : '',
      ].join('');
      alert(msg);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Toplu WMS aktarımı başarısız');
    } finally {
      setBulkRetryLoading(false);
    }
  };

  const pollAndTransferN11Orders = useCallback(async () => {
    if (!selectedWarehouseId || n11PollingInProgressRef.current) return;
    n11PollingInProgressRef.current = true;
    try {
      // WMS sayfasi kendi N11 sync'ini yapar: N11 -> SQL marketplace orders
      // (kullanici e-ticaret ekranina gecmeden akisin devam etmesi icin).
      const companyId = resolveCompanyId();
      if (companyId) {
        try {
          const nodeResponse = await n11Api.post('/api/n11/orders/sync', { size: 50 });
          const nodeData = nodeResponse.data;
          const latestN11Orders = nodeData?.success && Array.isArray(nodeData?.orders)
            ? nodeData.orders
            : [];

          if (latestN11Orders.length > 0) {
            const syncPayload = mapN11OrdersToMarketplaceSyncPayload(latestN11Orders);
            const syncResponse = await ecommerceApi.post('/api/ecommerce/orders/marketplace/sync', {
              MarketplaceSource: 'n11',
              Orders: syncPayload
            });
            console.log('[WMS N11 AUTO] Marketplace sync result:', syncResponse.data);
          }
        } catch (syncError) {
          console.error('[WMS N11 AUTO] N11 sync error:', syncError);
        }
      } else {
        console.warn('[WMS N11 AUTO] CompanyId bulunamadi, N11 sync atlandi.');
      }

      const marketplaceResponse = await ecommerceApi.get('/api/ecommerce/orders/marketplace?source=n11');
      const marketplaceData = marketplaceResponse.data;
      const n11Orders = Array.isArray(marketplaceData?.data) ? marketplaceData.data : [];

      const candidateOrderIds = n11Orders
        .filter((order: any) => {
          const normalized = String(order?.orderStatus || '').trim().toLowerCase();
          return order?.id && N11_ELIGIBLE_STATUSES.has(normalized);
        })
        .map((order: any) => String(order.id));

      console.log('[WMS N11 AUTO] Poll result:', {
        warehouseId: selectedWarehouseId,
        totalN11Orders: n11Orders.length,
        eligibleOrderCount: candidateOrderIds.length,
      });

      if (candidateOrderIds.length === 0) return;

      const statuses = await orderTransferService.getTransferStatusBulk(candidateOrderIds);
      const transferredIds = new Set(
        (statuses || [])
          .filter((s) => s.isTransferred)
          .map((s) => s.ecommerceOrderId)
      );
      const now = Date.now();
      const pendingTransferIds = candidateOrderIds.filter((id: string) => {
        if (transferredIds.has(id)) return false;
        const retryAfter = failedTransferCooldownRef.current[id] || 0;
        return now >= retryAfter;
      });
      console.log('[WMS N11 AUTO] Transfer candidates:', {
        alreadyTransferred: transferredIds.size,
        pendingTransferCount: pendingTransferIds.length,
      });
      if (pendingTransferIds.length === 0) return;

      const result = await orderTransferService.transferBulk({
        ecommerceOrderIds: pendingTransferIds,
        warehouseId: selectedWarehouseId
      });
      console.log('[WMS N11 AUTO] Transfer bulk response:', result);

      // Basarisiz siparisleri kisa sureli cooldown'a al ki her 15 sn ayni hata spam olmasin.
      const failedRows = (result?.data || []).filter((row) => !row.success);
      if (failedRows.length > 0) {
        failedRows.forEach((row) => {
          failedTransferCooldownRef.current[row.orderId] = Date.now() + N11_FAILED_RETRY_COOLDOWN_MS;
        });
        console.warn(
          '[WMS N11 AUTO] Failed transfer reasons:',
          failedRows.slice(0, 10).map((row) => ({ orderId: row.orderId, error: row.error }))
        );
      }

      // Basarili olanlar icin cooldown temizle.
      (result?.data || [])
        .filter((row) => row.success)
        .forEach((row) => {
          delete failedTransferCooldownRef.current[row.orderId];
        });

      if ((result?.totalTransferred || 0) > 0) {
        await Promise.all([loadOrders(), loadSummary()]);
      }
    } catch (error) {
      // Sessizce gecmek yerine konsolda gorelim; UI'yi yine bloklamayiz.
      console.error('[WMS N11 AUTO] Polling/transfer error:', error);
    } finally {
      n11PollingInProgressRef.current = false;
    }
  }, [selectedWarehouseId, loadOrders, loadSummary]);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    console.log('[WMS N11 AUTO] ecommerceApi baseURL:', (ecommerceApi.defaults.baseURL || '').toString());

    // İlk açılışta bir kez hemen tara
    pollAndTransferN11Orders();

    const timer = setInterval(() => {
      pollAndTransferN11Orders();
    }, N11_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [selectedWarehouseId, pollAndTransferN11Orders]);

  useEffect(() => {
    setSelectedOrderIds((prev) => {
      const validIds = new Set(
        displayedOrders.filter(isOrderSelectable).map((o) => o.id)
      );
      return new Set(Array.from(prev).filter((id) => validIds.has(id)));
    });
  }, [displayedOrders]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" fontWeight={700}>Sipariş Havuzu</Typography>
          <GuideButton guideKey="wms.orderPool" />
        </Box>
        <FormControl
          size="small"
          sx={{
            minWidth: 230,
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        >
          <InputLabel>Depo</InputLabel>
          <Select
            value={selectedWarehouseId}
            label="Depo"
            onChange={(e) => handleWarehouseChange(e.target.value)}
            startAdornment={<WarehouseIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />}
          >
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <OrderSummaryCards summary={summary} onFilterClick={handleSummaryFilterClick} />

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        <OrderFilters
          status={statusFilter}
          orderType={typeFilter}
          search={searchText}
          onStatusChange={handleStatusFilterChange}
          onOrderTypeChange={handleTypeFilterChange}
          onSearchChange={handleSearchChange}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {ORDER_SOURCE_BUTTONS.map((source) => {
            const active = activeFilter === source.id;
            return (
              <React.Fragment key={source.id}>
                <Button
                  size="small"
                  variant={active ? 'contained' : 'outlined'}
                  color={active ? 'primary' : 'inherit'}
                  onClick={() => setActiveFilter(source.id)}
                  sx={{
                    borderRadius: 2,
                    minWidth: 96,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: active ? 'primary.main' : (theme) => alpha(theme.palette.primary.main, 0.28),
                    color: active ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: active ? 'primary.dark' : (theme) => alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      mr: 0.75,
                      bgcolor: SOURCE_BRAND_META[source.id].bg,
                      color: SOURCE_BRAND_META[source.id].fg,
                    }}
                  >
                    {SOURCE_BRAND_META[source.id].short}
                  </Box>
                  {source.label}
                </Button>
                {source.id === 'all' && (() => {
                  const inWaveActive = activeFilter === 'inWave';
                  return (
                    <Button
                      size="small"
                      variant={inWaveActive ? 'contained' : 'outlined'}
                      color={inWaveActive ? 'warning' : 'inherit'}
                      onClick={() => setActiveFilter('inWave')}
                      sx={{
                        borderRadius: 2,
                        minWidth: 132,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: inWaveActive ? 'warning.main' : (theme) => alpha(theme.palette.warning.main, 0.42),
                        color: inWaveActive ? 'warning.contrastText' : 'warning.dark',
                        '&:hover': {
                          borderColor: 'warning.main',
                          bgcolor: inWaveActive ? 'warning.dark' : (theme) => alpha(theme.palette.warning.main, 0.08),
                        },
                      }}
                    >
                      İş Emrindekiler ({summary?.inWaveOrders ?? 0})
                    </Button>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlaylistAddCheckIcon />}
            disabled={selectedOrderIds.size === 0}
            onClick={() => setIsWaveModalOpen(true)}
          >
            Seçilenleri İş Emrine Ata ({selectedOrderIds.size})
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Manuel Sipariş
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="success"
            disabled={bulkRetryLoading}
            onClick={handleBulkRetryWmsTransfer}
            title="Stokta olan ama havuza düşmemiş e-ticaret siparişlerini otomatik olarak WMS'e aktar"
          >
            {bulkRetryLoading ? 'Aktarılıyor…' : 'E-Ticaret → WMS'}
          </Button>
          <IconButton
            size="small"
            onClick={handleOpenSettingsMenu}
            sx={{ border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 1.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={settingsMenuAnchorEl}
            open={Boolean(settingsMenuAnchorEl)}
            onClose={handleCloseSettingsMenu}
          >
            <MenuItem onClick={handleOpenWaveSettingsModal}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                <TuneIcon fontSize="small" />
              </Box>
              İş Emri Ayarları
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <OrderTable
            orders={displayedOrders}
            selectedIds={selectedOrderIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            isRowSelectable={isOrderSelectable}
            onViewDetail={handleViewDetail}
            onCancel={handleCancelRequest}
            page={page}
            pageSize={pageSize}
            totalCount={activeFilter === 'all' || activeFilter === 'inWave' ? totalCount : displayedOrders.length}
            onPageChange={setPage}
          />
        )}
      </Paper>

      <CreateOrderModal open={isCreateModalOpen} warehouseId={selectedWarehouseId}
        initialSource={sourceFilter === 'all' ? 'ecommerce' : sourceFilter}
        onClose={() => setIsCreateModalOpen(false)} onCreated={handleOrderCreated} />

      <OrderDetailModal open={isDetailModalOpen} orderId={selectedOrderForDetail}
        warehouseId={selectedWarehouseId} onClose={() => setIsDetailModalOpen(false)}
        onCancelled={handleDetailCancelled} />

      <CreateWaveModal open={isWaveModalOpen} warehouseId={selectedWarehouseId}
        orders={selectedOrders} waveLimits={waveLimits} onClose={() => setIsWaveModalOpen(false)}
        onCreated={handleWaveCreated} />

      <Dialog open={isWaveSettingsModalOpen} onClose={handleCloseWaveSettingsModal} maxWidth="xs" fullWidth>
        <DialogTitle>İş Emri Ayarları</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <TextField
              size="small"
              label="Max Sipariş Sayısı"
              type="number"
              value={waveLimits.maxOrders}
              onChange={(e) => handleLimitFieldChange('maxOrders', e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              size="small"
              label="Max Toplam Ürün Adedi"
              type="number"
              value={waveLimits.maxItems}
              onChange={(e) => handleLimitFieldChange('maxItems', e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              size="small"
              label="Max Toplam Desi"
              type="number"
              value={waveLimits.maxTotalDesi}
              onChange={(e) => handleLimitFieldChange('maxTotalDesi', e.target.value)}
              inputProps={{ min: 0, step: '0.001' }}
            />
            <Typography variant="caption" color="text.secondary">
              Bu ayarlar sipariş havuzunda saklanır. Sonraki adımda iş emri oluşturma kurallarına bağlanabilir.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWaveSettingsModal}>İptal</Button>
          <Button variant="contained" onClick={handleSaveWaveSettings}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!cancelDialogId} onClose={() => setCancelDialogId(null)}>
        <DialogTitle>Sipariş İptali</DialogTitle>
        <DialogContent>
          <Typography>Bu siparişi iptal etmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogId(null)}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancelLoading}>
            {cancelLoading ? <CircularProgress size={18} /> : 'İptal Et'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmsOrderPool;
